import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/main.css';
import useAuthStore from './stores/authStore';
import useThemeStore from './stores/themeStore';
import { useBranding } from './contexts/BrandingContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import RegistrationPending from './pages/RegistrationPending';
import Layout from './components/Layout';
import Home from './pages/Home';
import ArchivePlayback from './pages/ArchivePlayback';
import ArchivePlaybackNew from './pages/ArchivePlaybackNew';
import AccessControl from './pages/AccessControl';
import Intercoms from './pages/Intercoms';
import IntercomView from './pages/IntercomView';
import RegistrationNew from './pages/RegistrationNew';
import AdminSettings from './pages/AdminSettings';
import PlatformAdmin from './pages/PlatformAdmin';
import ShabbatSettings from "./pages/ShabbatSettings";
import AccessReview from "./pages/AccessReview";
import AuditLogViewer from "./pages/AuditLogViewer";
import MdDiscovery from "./pages/MdDiscovery";
import MdDashboard from "./pages/MdDashboard";
import MdHealth from "./pages/MdHealth";
import ServerHealth from "./pages/ServerHealth";
import MdMonitoredDevices from "./pages/MdMonitoredDevices";
import MdMonitoredDeviceDetail from "./pages/MdMonitoredDeviceDetail";
import MdDeviceProxy from "./pages/MdDeviceProxy";
import MdDiscoveryDeviceDetail from "./pages/MdDiscoveryDeviceDetail";
import MdOperations from "./pages/MdOperations";
import Reports from "./pages/Reports";
import MdSwitches from "./pages/MdSwitches";
import MdSwitchDetail from "./pages/MdSwitchDetail";
import OnWatchInstances from "./pages/OnWatchInstances";
import OnWatchHealth from "./pages/OnWatchHealth";
import OnWatchWorkloadDetail from "./pages/OnWatchWorkloadDetail";
import OnWatchCameras from "./pages/OnWatchCameras";
import OnWatchCameraDetail from "./pages/OnWatchCameraDetail";
import OnWatchAlertRules from "./pages/OnWatchAlertRules";
import EntryFleet from "./pages/EntryFleet";
import EntryDeviceDetail from "./pages/EntryDeviceDetail";
import EntryProfiles from "./pages/EntryProfiles";
import EntryApkList from "./pages/EntryApkList";
import EntryApkDetail from "./pages/EntryApkDetail";
import UserProfile from "./pages/UserProfile";
import NotificationCenter from './pages/NotificationCenter';
import VideoWall from "./pages/VideoWall";
import MapPage from './pages/Map';
import GenetecPlayerTest from "./pages/GenetecPlayerTest";
import MapDetail from './pages/MapDetail';
import MapPlacement from './pages/MapPlacement';
import SurveyWizard from './pages/SurveyWizard';
import RecordingAssurance from "./pages/RecordingAssurance";
import RecordingAssuranceDetail from "./pages/RecordingAssuranceDetail";
import RecordingAssuranceWizard from "./pages/RecordingAssuranceWizard";
import RecordingAssuranceAccuracy from "./pages/RecordingAssuranceAccuracy";
import Analytics from "./pages/Analytics";
import Associations from './pages/Associations';
import Investigations from './pages/Investigations';
import InvestigationDetail from './pages/InvestigationDetail';
import SharedCaseView from './pages/SharedCaseView';
import IntercomNotificationManager from './components/IntercomNotificationManager';
import IntercomCallControls from './components/IntercomCallControls';
import PushMessageHandler from './components/PushMessageHandler';

function App() {
  const { checkAuth, isLoading } = useAuthStore();
  const { initTheme } = useThemeStore();
  const { refreshBranding } = useBranding();

  useEffect(() => { initTheme(); }, []);

  useEffect(() => {
    (async () => {
      await checkAuth();
      const state = useAuthStore.getState();
      const orgId = state.currentTenant?.organizationId || state.user?.organizationId;
      if (orgId) {
        try { await refreshBranding(orgId); } catch {}
      }
    })();
  }, []);

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/registration-pending" element={<RegistrationPending />} />
        <Route path="/share/:token" element={<SharedCaseView />} />
        <Route path="/video-wall" element={
          <ProtectedRoute>
            <VideoWall />
          </ProtectedRoute>
        } />
        
        <Route path="/" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Home />} />
          <Route path="cameras" element={<ArchivePlaybackNew />} />
          <Route path="archive" element={<ArchivePlaybackNew />} /> {/* Legacy route */}
          <Route path="access" element={
            <ProtectedRoute requiredRole="viewer">
              <AccessControl />
            </ProtectedRoute>
          } />
          <Route path="intercoms" element={
            <ProtectedRoute requiredRole="viewer" requiredPermission="canAnswerIntercom">
              <Intercoms />
            </ProtectedRoute>
          } />
          <Route path="intercom/:id" element={
            <ProtectedRoute requiredRole="viewer" requiredPermission="canAnswerIntercom">
              <IntercomView />
            </ProtectedRoute>
          } />
          <Route path="registration" element={<RegistrationNew />} />
          <Route path="admin" element={
            <ProtectedRoute requiredRole="admin">
              <AdminSettings />
            </ProtectedRoute>
          } />
          <Route path="shabbat" element={
            <ProtectedRoute requiredRole="viewer" requiredPermission="canActivateShabbat">
              <ShabbatSettings />
            </ProtectedRoute>
          } />
          <Route path="map" element={<MapPage />} />
          <Route path="investigations" element={<Investigations />} />
          <Route path="investigations/cases/:id" element={<InvestigationDetail />} />
          <Route path="investigations/associations" element={<Associations />} />
          <Route path="map/survey/new" element={<SurveyWizard />} />
          <Route path="map/survey/:id" element={<MapDetail />} />
          <Route path="map/survey/:id/place" element={<MapPlacement />} />
          <Route path="profile" element={<UserProfile />} />
          <Route path="access-review" element={
            <ProtectedRoute requiredRole="admin">
              <AccessReview />
            </ProtectedRoute>
          } />
          <Route path="audit-log" element={
            <ProtectedRoute requiredRole="admin">
              <AuditLogViewer />
            </ProtectedRoute>
          } />
          <Route path="notifications" element={<NotificationCenter />} />
          <Route path="genetec-test" element={<GenetecPlayerTest />} />
          <Route path="monitoring" element={
            <ProtectedRoute requiredRole="admin">
              <MdDashboard />
            </ProtectedRoute>
          } />
          <Route path="monitoring/discovery" element={
            <ProtectedRoute requiredRole="admin">
              <MdDiscovery />
            </ProtectedRoute>
          } />
          <Route path="monitoring/discovery/:id" element={
            <ProtectedRoute requiredRole="admin">
              <MdDiscoveryDeviceDetail />
            </ProtectedRoute>
          } />
          <Route path="monitoring/devices" element={
            <ProtectedRoute requiredRole="admin">
              <MdMonitoredDevices />
            </ProtectedRoute>
          } />
          <Route path="monitoring/devices/:id" element={
            <ProtectedRoute requiredRole="admin">
              <MdMonitoredDeviceDetail />
            </ProtectedRoute>
          } />
          <Route path="monitoring/manage/:agentId/:deviceIp/:port" element={
            <ProtectedRoute requiredRole="admin">
              <MdDeviceProxy />
            </ProtectedRoute>
          } />
          <Route path="monitoring/health" element={
            <ProtectedRoute requiredRole="admin">
              <MdHealth />
            </ProtectedRoute>
          } />
          <Route path="monitoring/network" element={
            <ProtectedRoute requiredRole="admin">
              <MdSwitches />
            </ProtectedRoute>
          } />
          <Route path="monitoring/network/:id" element={
            <ProtectedRoute requiredRole="admin">
              <MdSwitchDetail />
            </ProtectedRoute>
          } />
          <Route path="monitoring/switches" element={<Navigate to="/monitoring/network" replace />} />
          <Route path="monitoring/switches/:id" element={<Navigate to="/monitoring/network" replace />} />
          <Route path="monitoring/onwatch" element={
            <ProtectedRoute requiredRole="admin">
              <OnWatchInstances />
            </ProtectedRoute>
          } />
          <Route path="monitoring/onwatch/:id" element={
            <ProtectedRoute requiredRole="admin">
              <OnWatchHealth />
            </ProtectedRoute>
          } />
          <Route path="monitoring/onwatch/:id/workloads/:namespace/:workloadName" element={
            <ProtectedRoute requiredRole="admin">
              <OnWatchWorkloadDetail />
            </ProtectedRoute>
          } />
          <Route path="monitoring/onwatch/:id/cameras" element={
            <ProtectedRoute requiredRole="admin">
              <OnWatchCameras />
            </ProtectedRoute>
          } />
          <Route path="monitoring/onwatch/:id/cameras/:cameraId" element={
            <ProtectedRoute requiredRole="admin">
              <OnWatchCameraDetail />
            </ProtectedRoute>
          } />
          <Route path="monitoring/onwatch/:id/alert-rules" element={
            <ProtectedRoute requiredRole="admin">
              <OnWatchAlertRules />
            </ProtectedRoute>
          } />
          <Route path="analytics" element={
            <ProtectedRoute requiredRole="admin">
              <Analytics />
            </ProtectedRoute>
          } />
          <Route path="recording-assurance" element={
            <ProtectedRoute requiredRole="admin">
              <RecordingAssurance />
            </ProtectedRoute>
          } />
          
          <Route path="recording-assurance/accuracy" element={
            <ProtectedRoute requiredRole="admin">
              <RecordingAssuranceAccuracy />
            </ProtectedRoute>
          } />
          <Route path="recording-assurance/setup" element={
            <ProtectedRoute requiredRole="admin">
              <RecordingAssuranceWizard />
            </ProtectedRoute>
          } />
          <Route path="recording-assurance/:configId" element={
            <ProtectedRoute requiredRole="admin">
              <RecordingAssuranceDetail />
            </ProtectedRoute>
          } />
          <Route path="entry" element={
            <ProtectedRoute requiredRole="admin">
              <EntryFleet />
            </ProtectedRoute>
          } />
          <Route path="entry/device/:id" element={
            <ProtectedRoute requiredRole="admin">
              <EntryDeviceDetail />
            </ProtectedRoute>
          } />
          <Route path="entry/profiles" element={
            <ProtectedRoute requiredRole="admin">
              <EntryProfiles />
            </ProtectedRoute>
          } />
          <Route path="settings/entry/apks" element={
            <ProtectedRoute requiredRole="admin">
              <EntryApkList />
            </ProtectedRoute>
          } />
          <Route path="settings/entry/apks/:apkId" element={
            <ProtectedRoute requiredRole="admin">
              <EntryApkDetail />
            </ProtectedRoute>
          } />
          <Route path="platform" element={
            <ProtectedRoute requiredRole="admin">
              <PlatformAdmin />
            </ProtectedRoute>
          } />
          <Route path="server-health" element={
            <ProtectedRoute requiredRole="admin">
              <ServerHealth />
            </ProtectedRoute>
          } />
          <Route path="monitoring/operations" element={
            <ProtectedRoute requiredRole="admin">
              <MdOperations />
            </ProtectedRoute>
          } />
          <Route path="reports" element={
            <ProtectedRoute requiredRole="admin">
              <Reports />
            </ProtectedRoute>
          } />
        </Route>




        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      <ToastContainer
        position="bottom-right"
        theme="dark"
        autoClose={3000}
      />
    <IntercomNotificationManager />
    <IntercomCallControls />
    <PushMessageHandler />
    </BrowserRouter>
  );
}

export default App;
